import { ClickAwayListener, Popper } from '@mui/material';
import Box from '@mui/material/Box';
import { MouseEvent, useEffect, useState } from 'react';
import { RgbStringColorPicker } from 'react-colorful';
import { Color } from '~/domain/dicom/dicomstructureset';
import { extractRGBNumbers } from '~/domain/dicom/utils';
import { ROIState } from '~/state/atoms/structureSet/roiAtoms';

export default function StructureColorPicker({
    roi,
    onChange,
}: {
    roi: ROIState;
    onChange: (color: Color) => void;
}) {
    const [colorPickerAnchorEl, setColorPickerAnchorEl] =
        useState<HTMLElement | null>(null);

    useEffect(() => {
        const handleColorPickerKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleColorPickerCloseUndo();
            } else if (event.key === 'Enter') {
                handleColorPickerCloseSave();
            }
        };

        if (colorPickerAnchorEl) {
            document.addEventListener('keydown', handleColorPickerKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleColorPickerKeyDown);
        };
    }, [colorPickerAnchorEl]);

    const handleColorPickerChange = (newColor: string) => {
        const [R, G, B] = extractRGBNumbers(newColor);
        const newColorObj: Color = { R, G, B };
        onChange(newColorObj);
    };

    const handleColorPickerCloseUndo = () => {
        setColorPickerAnchorEl(null);
        onChange(roi.color);
    };

    const handleColorPickerCloseSave = () => {
        setColorPickerAnchorEl(null);
    };

    const handleColorPickerClick = (event: MouseEvent<HTMLElement>) => {
        if (colorPickerAnchorEl) {
            handleColorPickerCloseSave();
            setColorPickerAnchorEl(null);
        } else {
            setColorPickerAnchorEl(event.currentTarget);
        }
    };

    return (
        <Box mr={1} ml={0.5}>
            <Popper
                open={!!colorPickerAnchorEl}
                anchorEl={colorPickerAnchorEl}
                placement="top"
                disablePortal
            >
                <ClickAwayListener onClickAway={handleColorPickerCloseSave}>
                    <Box
                        sx={{
                            p: '.25em',
                            position: 'fixed',
                            bottom: '.5em',
                            left: '-2.5em',
                        }}
                    >
                        <RgbStringColorPicker
                            color={`rgb(${Object.values(roi.color)})`}
                            onChange={handleColorPickerChange}
                        />
                    </Box>
                </ClickAwayListener>
            </Popper>
            <Box
                height={12}
                width={12}
                borderRadius="100%"
                bgcolor={`rgb(${Object.values(roi.color)})`}
                sx={{ position: 'relative' }}
                onClick={handleColorPickerClick}
            />
        </Box>
    );
}
